import React, { useMemo, useCallback, useState, useEffect } from 'react'
import LayoutComponent from '../BaseScreen'
import ModernTableComponent from '../../Components/Tables/ModernTableComponent'
import TeamMemberPanel from '../../Components/Modals/TeamMemberPanel'
import { useAuth } from '../../Context/AuthContext'
import { useData } from '../../Context/DataContext'

const TeamScreen = () => {
  const { allUsers, currentProfile } = useAuth()
  const { currentTimeZone } = useData()
  const [selectedMember, setSelectedMember] = useState<any>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const canSeeInactive = currentProfile.privileges === 'dev' || currentProfile.privileges === 'owner'
  const canSeeAllDepartments = currentProfile.privileges === 'admin' || currentProfile.privileges === 'dev' || currentProfile.privileges === 'owner'
  const isManagerOrStaff = currentProfile.privileges === 'manager' || currentProfile.privileges === 'staff' || currentProfile.privileges === 'director'

  // Update selectedMember when allUsers refreshes (for real-time updates in the panel)
  useEffect(() => {
    if (selectedMember && allUsers) {
      const updatedMember = allUsers.find(
        (user: any) => user.userid === selectedMember.userid
      )
      if (updatedMember) {
        const hasChanged = JSON.stringify(updatedMember) !== JSON.stringify(selectedMember)
        if (hasChanged) {
          setSelectedMember(updatedMember)
        }
      }
    }
  }, [allUsers])

  // Transform and sort users
  const teamData = useMemo(() => {
    if (!allUsers) return []

    let usersToShow = canSeeInactive ? allUsers : allUsers.filter((user: any) => user.active)

    // Managers and staff can only see their own department
    if (isManagerOrStaff && currentProfile.department) {
      usersToShow = usersToShow.filter((user: any) => user.department === currentProfile.department)
    }

    const formatLastLogin = (dateStr: string | null) => {
      if (!dateStr) return 'Never'
      // Append Z to indicate UTC if not already present
      const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
      const date = new Date(utcDateStr)
      return date.toLocaleString('en-US', {
        timeZone: currentTimeZone || 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    }

    return [...usersToShow]
      .sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : ''
        const nameB = b.name ? b.name.toLowerCase() : ''
        return nameA.localeCompare(nameB)
      })
      .map((user: any) => ({
        ...user,
        statusText: user.active ? 'Active' : 'Inactive',
        privilegesBadge: user.privileges,
        departmentBadge: user.department,
        lastLoginText: formatLastLogin(user.last_login)
      }))
  }, [allUsers, canSeeInactive, isManagerOrStaff, currentProfile.department, currentTimeZone])

  // Column configuration for ModernTableComponent
  const columns = [
    {
      key: 'name',
      label: 'Name',
      type: 'assignee' as const,
      photoKey: 'profile_photo',
      width: '200px',
      primaryOnMobile: true
    },
    {
      key: 'email',
      label: 'Email',
      type: 'text' as const,
      width: '220px',
      hideOnMobile: true
    },
    // Only show department column for users who can see all departments
    ...(canSeeAllDepartments ? [{
      key: 'departmentBadge',
      label: 'Department',
      type: 'badge' as const,
      badgeType: 'facility' as const,
      width: '130px'
    }] : []),
    {
      key: 'privilegesBadge',
      label: 'Role',
      type: 'badge' as const,
      badgeType: 'status' as const,
      width: '100px'
    },
    {
      key: 'lastLoginText',
      label: 'Last Login',
      type: 'text' as const,
      width: '130px',
      hideOnMobile: true
    },
    ...(canSeeInactive ? [{
      key: 'statusText',
      label: 'Status',
      type: 'badge' as const,
      badgeType: 'status' as const,
      width: '100px'
    }] : [])
  ]

  // Tabs configuration - managers and staff only see their department (no tabs needed)
  const tabs = isManagerOrStaff
    ? [{ label: 'My Team', value: 'all' }]
    : [
        { label: 'All Members', value: 'all' },
        { label: 'Intake', value: 'intake' },
        { label: 'Administration', value: 'administration' },
        { label: 'Claims', value: 'claims' },
        { label: 'Follow Up', value: 'followup' },
        ...(canSeeInactive ? [{ label: 'Inactive', value: 'inactive' }] : [])
      ]

  // Tab filter function
  const tabFilterFn = useCallback((row: any, tabValue: string) => {
    switch (tabValue) {
      case 'inactive':
        return !row.active
      case 'intake':
      case 'administration':
      case 'claims':
      case 'followup':
      case 'dev':
      case 'bd':
        return row.department === tabValue && row.active !== false
      default:
        return canSeeInactive ? true : row.active !== false
    }
  }, [canSeeInactive])

  // Get tab counts
  const getTabCount = useCallback((tabValue: string, data: any[]) => {
    if (tabValue === 'all') return data.filter(row => canSeeInactive ? true : row.active !== false).length
    return data.filter(row => tabFilterFn(row, tabValue)).length
  }, [tabFilterFn, canSeeInactive])

  // Handle row click
  const handleRowClick = useCallback((member: any) => {
    setSelectedMember(member)
    setIsPanelOpen(true)
  }, [])

  // Close panel
  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false)
  }, [])

  return (
    <>
      <LayoutComponent
        header={null}
        content={
          <div className="w-full h-full px-2 md:px-4 py-2 md:py-4">
            <ModernTableComponent
              tabs={tabs}
              columns={columns}
              data={teamData}
              searchPlaceholder="Search team members..."
              showRowNumbers={false}
              tabFilterFn={tabFilterFn}
              getTabCount={getTabCount}
              onRowClick={handleRowClick}
              selectedRow={selectedMember}
              isLoading={!allUsers}
              skeletonRows={8}
            />
          </div>
        }
      />
      <TeamMemberPanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        member={selectedMember}
      />
    </>
  )
}

export default TeamScreen
